import { useCallback, useMemo } from "react"
import {
  Box,
  Stack,
  Grid,
  Button,
  useTheme,
  CardMedia,
  Typography,
} from "@mui/material"
import { useParams } from "react-router-dom"
import { useAppUI } from "providers/ui"
import { QRCode } from "components/QRCode"
import { EmptyData } from "components/EmptyData"
import { getIcon } from "utils/icon"
import { ServiceIcon } from "components/icons"

import { AppNavigation as DefaultAppNavigation } from "containers/kiosk-v"
import { useTranslation } from "providers/i18n"
import { usePostDetail } from "hooks/post/usePostDetail"
import { useMobileUrl } from "hooks/useMobileUrl"

export const LuxuryDetailKioskVPage = ({
  Layout = Box,
  AppNavigation = DefaultAppNavigation,
  additionalMenu = null,
}) => {
  const { goBack } = useAppUI()
  const theme = useTheme()
  const { t } = useTranslation()
  const params = useParams()
  const { luxuryId } = params
  const { post, navigateToLocation } = usePostDetail(luxuryId)
  const { mobileUrl } = useMobileUrl({ path: post?.locationUrl })
  const emptyIcon = useMemo(
    () => getIcon(theme?.assets?.empty?.luxury) || <ServiceIcon />,
    [theme]
  )
  const styleOverrides =
    theme.components[`LuxuryDetailPage`]?.styleOverrides || {}
  const {
    root,
    mainBox,
    button,
    buttonContainer,
    cardMediaStyle,
    logoWrapper,
    qrcodePrimaryText,
    qrcodeSecondaryText,
    emptyDataWrapper,
  } = styleOverrides

  const handleClickBack = useCallback(() => {
    goBack()
  }, [goBack])
  return (
    <>
      <Layout>
        <Box sx={root}>
          {post?.id ? (
            <Stack sx={{ height: "100%" }}>
              <Box
                sx={{
                  ...mainBox,
                  backgroundImage: `url(${post?.coverImage})`,
                }}
              >
                {/* Logo */}
                <Box sx={logoWrapper}>
                  <CardMedia
                    component="img"
                    src={post?.locationLinkInfo?.image}
                  />
                </Box>

                {/* Action */}
                <Box key="app-navigation" sx={buttonContainer}>
                  <Stack spacing={0.5}>
                    <QRCode url={mobileUrl} />
                    <Stack spacing={0.5}>
                      <Typography
                        variant="subtitle2"
                        component="p"
                        sx={qrcodePrimaryText}
                      >
                        {t("Scan Here")}
                      </Typography>
                      <Typography
                        variant="caption"
                        component="p"
                        sx={qrcodeSecondaryText}
                      >
                        {t("For Mobile")}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing={1.25}>
                    <Button
                      variant="outlined"
                      sx={{
                        ...button,
                        backgroundColor: "transparent",
                        color: "common.white",
                      }}
                      onClick={handleClickBack}
                    >
                      {t("Back")}
                    </Button>
                    {post?.hasLocation && (
                      <Button
                        variant="contained"
                        sx={button}
                        className="highlight"
                        onClick={navigateToLocation}
                      >
                        {t("View Location")}
                      </Button>
                    )}
                  </Stack>
                </Box>
              </Box>

              {post?.gallery && post?.gallery.length > 0 && (
                <Grid container>
                  {(post?.gallery || []).slice(0, 4).map((item, idx) => {
                    return (
                      <Grid item xs={3} key={idx}>
                        <CardMedia
                          component="img"
                          src={item}
                          sx={cardMediaStyle}
                        />
                      </Grid>
                    )
                  })}
                </Grid>
              )}
            </Stack>
          ) : (
            <Box sx={emptyDataWrapper}>
              <EmptyData
                icon={emptyIcon}
                primary={t(`No Current Luxury`)}
                secondary={t("Check Back Soon")}
              />
            </Box>
          )}
        </Box>
      </Layout>
    </>
  )
}
